import { FC, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { GradientPage } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Text, useModalControl } from '@frontend/design-system';
import { Schedule1On1TrainingData } from '../../constants';
import { ScheduleTrainingTrackingIds } from '../../tracking-ids';
import { ScheduleTrainingModal } from '../schedule-training-modal';

interface ScheduleTrainingTaskCardUIProps {
  containerStyles?: Interpolation<Theme>;
  locationId: string;
  isVisible: boolean;
  isMutating: boolean;
  onHideTask: () => void;
}

export const ScheduleTrainingTaskCardUI: FC<ScheduleTrainingTaskCardUIProps> = ({
  containerStyles,
  locationId,
  isVisible,
  isMutating,
  onHideTask,
}) => {
  const { t } = useTranslation('scheduleTraining');

  const { modalProps: scheduleTrainingModalProps, openModal: openScheduleTrainingModal } = useModalControl();

  const renderAction = useMemo(() => {
    return (
      <Button
        variant='tertiary'
        trackingId={ScheduleTrainingTrackingIds.scheduleTrainingBtn}
        onClick={openScheduleTrainingModal}
      >
        <Text as='span' weight='bold' size='large'>
          {t('Schedule Training')}
        </Text>
      </Button>
    );
  }, [openScheduleTrainingModal, t]);

  const renderCloseAction = useMemo(() => {
    return (
      <Button
        variant='secondary'
        iconName='x-small'
        aria-label='Close'
        trackingId={ScheduleTrainingTrackingIds.dismissBtn}
        onClick={onHideTask}
      />
    );
  }, [onHideTask]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div
        css={[styles.containerCard, containerStyles]}
        data-testid='schedule-training-task-card'
        data-trackingid={ScheduleTrainingTrackingIds.scheduleTrainingCard}
      >
        <ContentLoader show={isMutating} />
        <GradientPage gradientColor1={theme.colors.secondary.seaweed5} gradientColor2={theme.colors.indigo10}>
          <div css={styles.contentContainer}>
            <div css={styles.headerXS}>
              <Icon name='weave-logo' size={24} color='light' />
              <Text size='large' weight='bold' color='light' css={{ flex: 1 }}>
                {Schedule1On1TrainingData.title}
              </Text>
              {renderCloseAction}
            </div>
            <div css={styles.contentXS}>
              <Text size='medium' color='light'>
                {Schedule1On1TrainingData.description}
              </Text>
              {renderAction}
            </div>
            <div css={styles.contentSM}>
              <Icon name='weave-logo' size={24} color='light' />
              <div css={styles.textContent}>
                <Text size='large' weight='bold' color='light'>
                  {Schedule1On1TrainingData.title}
                </Text>
                <Text size='medium' color='light'>
                  {Schedule1On1TrainingData.description}
                </Text>
              </div>
              {renderAction}
              {renderCloseAction}
            </div>
          </div>
        </GradientPage>
      </div>
      <ScheduleTrainingModal locationId={locationId} {...scheduleTrainingModalProps} />
    </>
  );
};

const styles = {
  containerCard: css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.large};
    padding: 0;
    box-shadow: ${theme.shadows.floating};
    position: relative;
    z-index: 0;
    overflow: hidden;
    container-type: inline-size;
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      padding: ${theme.spacing(1)};
    }
  `,
  textContent: css`
    display: flex;
    flex-direction: column;
    flex: 1;
  `,
  headerXS: css`
    display: none;
    @container (max-width: ${breakpoints.small.min}px) {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
    }
  `,
  contentXS: css`
    display: none;
    @container (max-width: ${breakpoints.small.min}px) {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: ${theme.spacing(1)};
    }
  `,
  contentSM: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      display: none;
    }
  `,
};
